import React, { useEffect, useMemo, useState } from 'react';
import { Check, Eye, Grid3X3, Layout, MoveRight, RotateCcw, Save, X } from 'lucide-react';
import { CARD_THEMES } from './ActivityNode/cardThemes';
import { getAvailableColors, getLayoutTheme } from '../utils/layoutColors';

interface VisualSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  gridType: 'lines' | 'dots' | 'none';
  onGridTypeChange: (type: 'lines' | 'dots' | 'none') => void;
  gridColor: string;
  onGridColorChange: (color: string) => void;
  backgroundColor: string;
  onBackgroundColorChange: (color: string) => void;
  arrowColor: string;
  onArrowColorChange: (color: string) => void;
  currentLayout: string;
  onLayoutChange: (layout: string) => void;
  currentColor: string;
  onColorChange: (color: string) => void;
  // Factory VSM
  mapType?: string;
  vsmDesignStyle?: 'blueprint' | 'sketch' | 'technical';
  onVsmDesignStyleChange?: (style: 'blueprint' | 'sketch' | 'technical') => void;
  vsmCardColor?: string;
  onVsmCardColorChange?: (color: string) => void;
}

type VisualSection = 'canvas' | 'arrowColor' | 'layout' | 'cardColor';

const DEFAULT_BG_COLOR = '#223244';
const DEFAULT_GRID_COLOR = 'rgba(255,255,255,0.03)';
const DEFAULT_ARROW_COLOR = '#64748B';
type CardColorOption = {
  value: string;
  label: string;
  surface: string;
  accent: string;
  border: string;
  textColor: string;
  secondaryText: string;
  footerSurface: string;
};

const panelStyle: React.CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 4,
  background: 'var(--bg-surface)',
};

const GRID_TYPES = [
  { id: 'lines', label: 'Grade', description: 'Malha cartesiana contínua' },
  { id: 'none', label: 'Limpo', description: 'Canvas sem referências' },
] as const;

const BACKGROUNDS = [
  // Dark
  { value: '#223244', label: 'Workspace' },
  { value: '#18202A', label: 'Studio' },
  { value: '#202A35', label: 'Slate' },
  { value: '#0F172A', label: 'Blueprint' },
  { value: '#111827', label: 'Carbon' },
  { value: '#1E293B', label: 'Navy' },
  { value: '#334155', label: 'Aço' },
  { value: '#0D1117', label: 'Git dark' },
  { value: '#1A1A2E', label: 'Midnight' },
  { value: '#2D2D2D', label: 'Charcoal' },
  // Neutral dark (black theme)
  { value: '#111111', label: 'Black' },
  { value: '#1a1a1a', label: 'Dark Gray' },
  { value: '#222222', label: 'Graphite' },
  // Light
  { value: '#FDFEFF', label: 'Workspace L' },
  { value: '#FAFBFD', label: 'Light' },
  { value: '#F8FAFC', label: 'Neutro' },
  { value: '#F1F5F9', label: 'Office' },
  { value: '#F7F7F7', label: 'Cinza min.' },
  { value: '#FFFFFF', label: 'Branco' },
  // Special
  { value: '#FFF9E6', label: 'Kaizen' },
  { value: '#F2FBF7', label: 'Future' },
  { value: '#FFF7ED', label: 'Warm' },
  { value: '#F5F3FF', label: 'Lavender' },
  { value: '#FFF1F2', label: 'Rosé' },
  { value: '#F0FDF4', label: 'Mint' },
  { value: '#EFF6FF', label: 'Sky pale' },
  { value: '#FFFBEB', label: 'Creme' },
  { value: '#F0F4F8', label: 'Cool' },
];

const GRID_COLORS = [
  { value: 'rgba(255,255,255,0.03)', label: 'Fant. dk' },
  { value: 'rgba(255,255,255,0.05)', label: 'Sutil dk' },
  { value: 'rgba(255,255,255,0.08)', label: 'Claro dk' },
  { value: 'rgba(255,255,255,0.14)', label: 'Vis. dk' },
  { value: 'rgba(15,23,42,0.022)', label: 'Fant. lt' },
  { value: 'rgba(15,23,42,0.04)', label: 'Sutil lt' },
  { value: 'rgba(15,23,42,0.07)', label: 'Claro lt' },
  { value: 'rgba(15,23,42,0.12)', label: 'Vis. lt' },
  { value: '#E2E8F0', label: 'Suave' },
  { value: '#CBD5E1', label: 'Cinza cl.' },
  { value: '#94A3B8', label: 'Cinza' },
  { value: '#64748B', label: 'Slate' },
  { value: '#475569', label: 'Slate dk' },
  { value: '#334155', label: 'Slate ++' },
  { value: '#3B82F6', label: 'Azul' },
  { value: '#2563EB', label: 'Azul forte' },
  { value: '#14B8A6', label: 'Ciano' },
  { value: '#10B981', label: 'Verde' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#8B5CF6', label: 'Roxo' },
];

const ARROW_COLORS = [
  { value: '#64748B', label: 'Slate dk' },
  { value: 'rgba(0,0,0,0.6)', label: 'Slate lt' },
  { value: '#E2E8F0', label: 'Branco' },
  { value: '#94A3B8', label: 'Cinza' },
  { value: '#475569', label: 'Slate +' },
  { value: '#1E3A5F', label: 'Navy' },
  { value: '#3B82F6', label: 'Azul' },
  { value: '#2563EB', label: 'Azul fort.' },
  { value: '#10B981', label: 'Verde' },
  { value: '#14B8A6', label: 'Ciano' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#EF4444', label: 'Vermelho' },
  { value: '#F97316', label: 'Laranja' },
  { value: '#8B5CF6', label: 'Roxo' },
  { value: '#EC4899', label: 'Rosa' },
  { value: '#111827', label: 'Preto' },
];

const LAYOUTS = [
  { id: 'default', label: 'Ficha', description: 'Faixa superior e corpo neutro' },
  { id: 'postit', label: 'Post-it', description: 'Bloco sólido para destaque rápido' },
  { id: 'minimal', label: 'Compacto', description: 'Leitura limpa com contorno leve' },
  { id: 'classic', label: 'VSM', description: 'Caixa forte para operação formal' },
  { id: 'neon', label: 'Neon', description: 'Dark industrial com brilho neon' },
  { id: 'blueprint', label: 'Blueprint', description: 'Planta técnica de engenharia' },
  { id: 'origami', label: 'Origami', description: 'Papel dobrado com canto vinco' },
  { id: 'kanban', label: 'Kanban', description: 'Board card estilo Linear/Jira' },
  { id: 'ticket', label: 'Ticket', description: 'Bilhete com faixa colorida e picote' },
];

// Paleta de cores para ícones VSM Factory (blueprint + sketch)
const VSM_COLORS = [
  { value: '#7dd3fc', label: 'Sky' },
  { value: '#60a5fa', label: 'Azul' },
  { value: '#3b82f6', label: 'Azul forte' },
  { value: '#1d4ed8', label: 'Navy' },
  { value: '#38bdf8', label: 'Ciano cl.' },
  { value: '#06b6d4', label: 'Ciano' },
  { value: '#14b8a6', label: 'Teal' },
  { value: '#34d399', label: 'Verde cl.' },
  { value: '#10b981', label: 'Verde' },
  { value: '#4ade80', label: 'Lime' },
  { value: '#a3e635', label: 'Chartreuse' },
  { value: '#facc15', label: 'Amarelo' },
  { value: '#fb923c', label: 'Laranja' },
  { value: '#f87171', label: 'Vermelho cl.' },
  { value: '#ef4444', label: 'Vermelho' },
  { value: '#e879f9', label: 'Fúcsia' },
  { value: '#a78bfa', label: 'Roxo cl.' },
  { value: '#8b5cf6', label: 'Roxo' },
  { value: '#94a3b8', label: 'Slate' },
  { value: '#e2e8f0', label: 'Branco' },
];

const COLOR_LABELS: Record<string, string> = {
  white: 'Branco',
  'gray-10': 'Cinza 10',
  'gray-20': 'Cinza 20',
  gray: 'Cinza',
  'yellow-10': 'Amarelo 10',
  'yellow-20': 'Amarelo 20',
  yellow: 'Amarelo',
  'yellow-dark': 'Amarelo escuro',
  'red-10': 'Vermelho 10',
  'red-20': 'Vermelho 20',
  red: 'Vermelho',
  'red-dark': 'Vermelho escuro',
  'green-10': 'Verde 10',
  'green-20': 'Verde 20',
  green: 'Verde',
  'green-dark': 'Verde escuro',
  'blue-10': 'Azul 10',
  'blue-20': 'Azul 20',
  blue: 'Azul',
  'blue-dark': 'Azul escuro',
  emerald: 'Esmeralda',
  teal: 'Teal',
  purple: 'Roxo',
  orange: 'Laranja',
  amber: 'Amber',
  pink: 'Rosa',
  rose: 'Rose',
  slate: 'Slate',
  lightgreen: 'Verde claro',
  darkgreen: 'Verde profundo',
  lightblue: 'Azul claro',
  darkblue: 'Azul profundo',
  darkyellow: 'Amarelo profundo',
  darkred: 'Vermelho profundo',
  ocean: 'Ocean',
  forest: 'Forest',
  sunset: 'Sunset',
  lavender: 'Lavender',
  coral: 'Coral',
  midnight: 'Midnight',
  sage: 'Sage',
};

function formatColorLabel(value: string) {
  if (COLOR_LABELS[value]) return COLOR_LABELS[value];
  return value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function resolveCardColorOption(layout: string, color: string): CardColorOption {
  if (layout === 'classic' || layout === 'default') {
    const theme = CARD_THEMES[color] || CARD_THEMES.green;
    return {
      value: color,
      label: formatColorLabel(color),
      surface: layout === 'classic' ? theme.headerBg : '#FFFFFF',
      accent: theme.headerBg,
      border: theme.border,
      textColor: theme.textColor,
      secondaryText: theme.textColor === '#ffffff' ? 'rgba(255,255,255,0.8)' : '#64748B',
      footerSurface: layout === 'classic' ? theme.headerBg : '#E5E7EB',
    };
  }

  const theme = getLayoutTheme(layout as 'postit' | 'minimal', color) || getLayoutTheme(layout as 'postit' | 'minimal', 'white');

  return {
    value: color,
    label: formatColorLabel(color),
    surface: theme?.bg || '#FFFFFF',
    accent: theme?.accent || theme?.footerBg || theme?.border || '#CBD5E1',
    border: theme?.border || '#94A3B8',
    textColor: theme?.text || '#1F2937',
    secondaryText: theme?.textSecondary || '#64748B',
    footerSurface: theme?.footerBg || theme?.accent || theme?.border || '#E5E7EB',
  };
}

function normalizeColorValue(value: string | undefined) {
  return (value || '').replace(/\s+/g, '').toLowerCase();
}

function getCanvasStyle(backgroundColor: string, gridType: 'lines' | 'dots' | 'none', gridColor: string): React.CSSProperties {
  if (gridType === 'lines' || gridType === 'dots') {
    // Always render hierarchical line grid in preview
    return {
      backgroundColor,
      backgroundImage: `
        linear-gradient(to right, ${gridColor} 1px, transparent 1px),
        linear-gradient(to bottom, ${gridColor} 1px, transparent 1px)
      `.trim(),
      backgroundSize: '10px 10px',
    };
  }
  return { backgroundColor };
}

function renderCard(layout: string, palette: CardColorOption) {
  const borderWidth = layout === 'classic' || layout === 'minimal' ? 2 : 1.5;
  const titleColor = layout === 'classic' ? palette.textColor : '#0F172A';
  const labelColor = palette.secondaryText;
  const lineColor = '#D1D5DB';
  const bodyFill = layout === 'postit' ? palette.surface : '#FFFFFF';
  const coloredHeader = palette.accent;
  const cardStroke = layout === 'classic' ? '#111827' : palette.border;
  const headerText = palette.textColor;
  const metricFill = layout === 'default' ? palette.footerSurface : bodyFill;

  const renderTrash = (x: number, y: number, tinted = false) => (
    <g transform={`translate(${x}, ${y})`}>
      <rect x="0" y="0" width="13" height="13" rx="2.5" fill={tinted ? 'rgba(255,255,255,0.55)' : '#F3F4F6'} />
      <path d="M4 4.5H9M5 4.5V9M8 4.5V9M4.5 4.5L5 10H8L8.5 4.5M5.5 3.5H7.5" stroke="#94A3B8" strokeWidth="0.9" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </g>
  );

  const renderStatus = (x: number, y: number, light = false) => (
    <g transform={`translate(${x}, ${y})`}>
      <circle cx="3" cy="3" r="2.1" fill="#22C55E" />
      <text x="8" y="5.5" fontSize="5.5" fontWeight="600" fill={light ? 'rgba(255,255,255,0.82)' : '#64748B'}>
        0.0%
      </text>
    </g>
  );

  if (layout === 'classic') {
    return (
      <svg viewBox="0 0 160 84" className="h-full w-full" aria-hidden="true">
        <rect x="2" y="2" width="156" height="80" rx="3" fill={palette.surface} stroke={cardStroke} strokeWidth={borderWidth} />
        {renderStatus(10, 7)}
        <text x="80" y="30" fontSize="9.5" fontWeight="700" textAnchor="middle" fill={titleColor}>Nova Atividade</text>
        <line x1="4" y1="38" x2="156" y2="38" stroke={cardStroke} strokeWidth="1" />
        <line x1="4" y1="54" x2="156" y2="54" stroke={cardStroke} strokeWidth="1" />
        <line x1="4" y1="70" x2="156" y2="70" stroke={cardStroke} strokeWidth="1" />
        <text x="10" y="48" fontSize="5.8" fontWeight="700" fill={titleColor}>Quem:</text>
        <text x="42" y="48" fontSize="5.8" fontWeight="600" fill={titleColor}>-</text>
        <text x="10" y="64" fontSize="5.8" fontWeight="700" fill={titleColor}>Como:</text>
        <text x="46" y="64" fontSize="5.8" fontWeight="600" fill={titleColor}>-</text>
        <line x1="53.5" y1="70" x2="53.5" y2="80" stroke={cardStroke} strokeWidth="1" />
        <line x1="106.5" y1="70" x2="106.5" y2="80" stroke={cardStroke} strokeWidth="1" />
        <text x="28" y="77" fontSize="5.7" fontWeight="700" textAnchor="middle" fill={titleColor}>P: -</text>
        <text x="80" y="77" fontSize="5.7" fontWeight="700" textAnchor="middle" fill={titleColor}>TP: -</text>
        <text x="132" y="77" fontSize="5.7" fontWeight="700" textAnchor="middle" fill={titleColor}>TRA: -</text>
      </svg>
    );
  }

  if (layout === 'default') {
    return (
      <svg viewBox="0 0 160 84" className="h-full w-full" aria-hidden="true">
        <rect x="2" y="2" width="156" height="80" rx="5" fill="#FFFFFF" stroke={cardStroke} strokeWidth={1.6} />
        <rect x="3" y="3" width="154" height="28" rx="4" fill={coloredHeader} />
        {renderStatus(10, 7, headerText === '#FFFFFF')}
        {renderTrash(141, 8, true)}
        <text x="10" y="25" fontSize="6.5" fontWeight="800" fill={headerText}>NOVA ATIVIDADE</text>
        <rect x="3" y="31" width="154" height="50" rx="0" fill="#F7F7F8" />
        <text x="14" y="46" fontSize="5.5" fontWeight="700" fill={labelColor}>QUEM</text>
        <text x="49" y="46" fontSize="5.8" fontWeight="700" fill={titleColor}>-</text>
        <text x="14" y="60" fontSize="5.5" fontWeight="700" fill={labelColor}>COMO</text>
        <text x="49" y="60" fontSize="5.8" fontWeight="700" fill={titleColor}>-</text>
        <line x1="14" y1="65" x2="146" y2="65" stroke={lineColor} strokeWidth="1" />
        <rect x="14" y="67" width="132" height="11" rx="3" fill={metricFill} />
        <line x1="58" y1="67" x2="58" y2="78" stroke={lineColor} strokeWidth="1" />
        <line x1="102" y1="67" x2="102" y2="78" stroke={lineColor} strokeWidth="1" />
        <text x="36" y="72.5" fontSize="4.8" fontWeight="700" textAnchor="middle" fill={labelColor}>P</text>
        <text x="80" y="72.5" fontSize="4.8" fontWeight="700" textAnchor="middle" fill={labelColor}>TP</text>
        <text x="124" y="72.5" fontSize="4.8" fontWeight="700" textAnchor="middle" fill={labelColor}>TRA</text>
        <text x="36" y="77.5" fontSize="7" fontWeight="800" textAnchor="middle" fill={titleColor}>1</text>
        <text x="80" y="77.5" fontSize="7" fontWeight="800" textAnchor="middle" fill={titleColor}>0</text>
        <text x="124" y="77.5" fontSize="7" fontWeight="800" textAnchor="middle" fill={titleColor}>0</text>
      </svg>
    );
  }

  if (layout === 'minimal') {
    return (
      <svg viewBox="0 0 160 84" className="h-full w-full" aria-hidden="true">
        <rect x="2" y="2" width="156" height="80" rx="6" fill={palette.surface} stroke={palette.border} strokeWidth={1.7} />
        {renderStatus(10, 7)}
        {renderTrash(141, 8)}
        <rect x="4" y="16" width="152" height="14" rx="4" fill={palette.accent} opacity="0.18" />
        <text x="80" y="25" fontSize="9.5" fontWeight="700" textAnchor="middle" fill={titleColor}>Nova Atividade</text>
        <text x="14" y="44" fontSize="5.4" fontWeight="700" fill={labelColor}>QUEM</text>
        <text x="82" y="44" fontSize="5.4" fontWeight="700" fill={labelColor}>COMO</text>
        <text x="14" y="55" fontSize="5.8" fontWeight="700" fill={titleColor}>-</text>
        <text x="82" y="55" fontSize="5.8" fontWeight="700" fill={titleColor}>-</text>
        <rect x="14" y="61" width="132" height="17" rx="4" fill={palette.footerSurface} opacity="0.9" />
        <line x1="58" y1="61" x2="58" y2="78" stroke={lineColor} strokeWidth="1" />
        <line x1="102" y1="61" x2="102" y2="78" stroke={lineColor} strokeWidth="1" />
        <text x="36" y="68" fontSize="4.8" fontWeight="700" textAnchor="middle" fill={labelColor}>P</text>
        <text x="80" y="68" fontSize="4.8" fontWeight="700" textAnchor="middle" fill={labelColor}>TP</text>
        <text x="124" y="68" fontSize="4.8" fontWeight="700" textAnchor="middle" fill={labelColor}>TRA</text>
        <text x="36" y="76" fontSize="7" fontWeight="800" textAnchor="middle" fill={titleColor}>1</text>
        <text x="80" y="76" fontSize="7" fontWeight="800" textAnchor="middle" fill={titleColor}>0</text>
        <text x="124" y="76" fontSize="7" fontWeight="800" textAnchor="middle" fill={titleColor}>0</text>
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 160 84" className="h-full w-full" aria-hidden="true">
      <rect x="2" y="2" width="156" height="80" rx="7" fill={bodyFill} stroke={palette.border} strokeWidth={1.8} />
      {renderStatus(10, 7)}
      <text x="80" y="28" fontSize="8.6" fontWeight="800" textAnchor="middle" fill={titleColor}>Nova Atividade</text>
      <line x1="3" y1="35" x2="157" y2="35" stroke={palette.border} strokeWidth="1" />
      <line x1="3" y1="52" x2="157" y2="52" stroke={palette.border} strokeWidth="1" />
      <line x1="3" y1="67" x2="157" y2="67" stroke={palette.border} strokeWidth="1" />
      <text x="12" y="46" fontSize="5.7" fontWeight="800" fill={titleColor}>QUEM:</text>
      <text x="48" y="46" fontSize="5.7" fontWeight="700" fill={titleColor}>-</text>
      <text x="12" y="61" fontSize="5.7" fontWeight="800" fill={titleColor}>COMO:</text>
      <text x="48" y="61" fontSize="5.7" fontWeight="700" fill={titleColor}>-</text>
      <line x1="53.5" y1="67" x2="53.5" y2="82" stroke={palette.border} strokeWidth="1" />
      <line x1="106.5" y1="67" x2="106.5" y2="82" stroke={palette.border} strokeWidth="1" />
      <text x="28" y="73" fontSize="5.3" fontWeight="700" textAnchor="middle" fill={titleColor}>P</text>
      <text x="80" y="73" fontSize="5.3" fontWeight="700" textAnchor="middle" fill={titleColor}>TP</text>
      <text x="132" y="73" fontSize="5.3" fontWeight="700" textAnchor="middle" fill={titleColor}>TRA</text>
      <text x="28" y="80" fontSize="7" fontWeight="800" textAnchor="middle" fill={titleColor}>0</text>
      <text x="80" y="80" fontSize="7" fontWeight="800" textAnchor="middle" fill={titleColor}>-</text>
      <text x="132" y="80" fontSize="7" fontWeight="800" textAnchor="middle" fill={titleColor}>-</text>
    </svg>
  );
}

export const VisualSettingsModal: React.FC<VisualSettingsModalProps> = ({
  isOpen,
  onClose,
  gridType,
  onGridTypeChange,
  gridColor,
  onGridColorChange,
  backgroundColor,
  onBackgroundColorChange,
  arrowColor,
  onArrowColorChange,
  currentLayout,
  onLayoutChange,
  currentColor,
  onColorChange,
  mapType,
  vsmDesignStyle,
  onVsmDesignStyleChange,
  vsmCardColor,
  onVsmCardColorChange,
}) => {
  const isFactory = mapType === 'FACTORY';
  const [activeSection, setActiveSection] = useState<VisualSection>('canvas');
  const [localGridType, setLocalGridType] = useState(gridType);
  const [localGridColor, setLocalGridColor] = useState(gridColor);
  const [localBgColor, setLocalBgColor] = useState(backgroundColor);
  const [localArrowColor, setLocalArrowColor] = useState(arrowColor);
  const [localLayout, setLocalLayout] = useState(currentLayout);
  const [localCardColor, setLocalCardColor] = useState(currentColor);
  const [localVsmStyle, setLocalVsmStyle] = useState<'blueprint' | 'sketch' | 'technical'>(vsmDesignStyle ?? 'sketch');
  const [localVsmCardColor, setLocalVsmCardColor] = useState(vsmCardColor ?? '#7dd3fc');

  useEffect(() => {
    if (!isOpen) return;
    setActiveSection('canvas');
    setLocalGridType(gridType);
    setLocalGridColor(gridColor);
    setLocalBgColor(backgroundColor);
    setLocalArrowColor(arrowColor);
    setLocalLayout(currentLayout);
    setLocalCardColor(currentColor);
    setLocalVsmStyle(vsmDesignStyle ?? 'sketch');
    setLocalVsmCardColor(vsmCardColor ?? '#7dd3fc');
  }, [isOpen, gridType, gridColor, backgroundColor, arrowColor, currentLayout, currentColor, vsmDesignStyle, vsmCardColor]);

  // Reset activeSection when mapType changes so 'layout' tab (Office-only) doesn't stay active in Factory
  useEffect(() => {
    if (isFactory && activeSection === 'layout') {
      setActiveSection('cardColor');
    }
  }, [isFactory, activeSection]);

  const availableCardColors = useMemo(() => {
    if (localLayout === 'classic' || localLayout === 'default') {
      return Object.keys(CARD_THEMES).map((color) => resolveCardColorOption(localLayout, color));
    }
    return getAvailableColors(localLayout as 'postit' | 'minimal').map((color) => resolveCardColorOption(localLayout, color));
  }, [localLayout]);

  const palette = useMemo(
    () => availableCardColors.find((option) => option.value === localCardColor) || availableCardColors[0],
    [availableCardColors, localCardColor]
  );
  const canvasStyle = useMemo(() => getCanvasStyle(localBgColor, localGridType, localGridColor), [localBgColor, localGridType, localGridColor]);

  const handleSave = () => {
    onGridTypeChange(localGridType);
    onGridColorChange(localGridColor);
    onBackgroundColorChange(localBgColor);
    onArrowColorChange(localArrowColor);
    if (!isFactory) {
      onLayoutChange(localLayout);
      onColorChange(localCardColor);
    } else {
      onVsmDesignStyleChange?.(localVsmStyle);
      onVsmCardColorChange?.(localVsmCardColor);
    }
    onClose();
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-3" style={{ background: 'rgba(2, 6, 23, 0.72)' }}>
      <div className="flex h-[min(660px,calc(100vh-24px))] w-full max-w-[700px] lg:max-w-[860px] flex-col overflow-hidden rounded-[6px]" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: '0 20px 60px rgba(2, 6, 23, 0.28)', color: 'var(--text-primary)' }}>
        <div className="flex items-center justify-between px-4 py-2.5" style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border-default)' }}>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-[4px]" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)' }}><Eye size={18} style={{ color: 'var(--text-secondary)' }} /></div>
            <div><div className="text-sm font-semibold">Configurações visuais do mapa</div><div className="text-[11px] uppercase tracking-[0.08em]" style={{ color: 'var(--text-secondary)' }}>{isFactory ? 'Canvas, grade, setas e estilo VSM Factory' : 'Canvas, grade, setas e padrão visual dos cards'}</div></div>
          </div>
          <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-[4px]" style={{ border: '1px solid var(--border-default)', background: 'var(--bg-surface)' }}><X size={16} style={{ color: 'var(--text-secondary)' }} /></button>
        </div>

        <div className="flex items-center gap-2 px-4 py-2" style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border-default)' }}>
          {[
            { id: 'canvas', label: 'Canvas', icon: Grid3X3 },
            { id: 'arrowColor', label: 'Setas', icon: MoveRight },
            ...(!isFactory ? [{ id: 'layout', label: 'Layout', icon: Layout }] : []),
            { id: 'cardColor', label: isFactory ? 'Estilo VSM' : 'Cor card', icon: Check },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = activeSection === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveSection(tab.id as VisualSection)}
                className="flex items-center gap-1.5 rounded-[4px] px-3 py-1.5 text-[11px] font-medium"
                style={{ border: `1px solid ${active ? '#3B82F6' : 'var(--border-default)'}`, background: active ? 'rgba(59,130,246,0.12)' : 'var(--bg-surface)' }}
              >
                <Icon size={13} style={{ color: active ? '#60A5FA' : 'var(--text-secondary)' }} />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="grid min-h-0 flex-1 gap-0 overflow-hidden lg:grid-cols-[minmax(0,1fr)_200px]">
          <main className="min-h-0 overflow-y-auto px-4 py-3 pb-4">
            {activeSection === 'canvas' && (
              <section className="flex min-h-0 flex-col gap-4 rounded-[4px] p-4" style={panelStyle}>
                {/* Tipo de grade */}
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--text-secondary)' }}>Tipo de grade</div>
                  <div className="mt-1 text-[11px]" style={{ color: 'var(--text-secondary)' }}>Escolha a referência visual base do canvas.</div>
                  <div className="mt-3 grid gap-2" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                    {GRID_TYPES.map((option) => (
                      <button key={option.id} type="button" onClick={() => setLocalGridType(option.id)} className="rounded-[4px] p-2.5 text-left" style={{ border: `1px solid ${localGridType === option.id ? '#3B82F6' : 'var(--border-default)'}`, background: localGridType === option.id ? 'rgba(59,130,246,0.12)' : 'var(--bg-elevated)' }}>
                        <div className="mb-2 h-12 overflow-hidden rounded-[4px] border" style={{ borderColor: 'var(--border-default)' }}><div className="h-full w-full" style={getCanvasStyle(localBgColor, option.id, localGridColor)} /></div>
                        <div className="text-[11px] font-semibold">{option.label}</div>
                        <div className="mt-0.5 text-[10px] leading-tight" style={{ color: 'var(--text-secondary)' }}>{option.description}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Divider */}
                <div style={{ height: 1, background: 'var(--border-default)' }} />

                {/* Fundo do canvas */}
                <div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--text-secondary)' }}>Fundo do canvas</div>
                      <div className="mt-0.5 text-[11px]" style={{ color: 'var(--text-secondary)' }}>Superfície de trabalho do editor.</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setLocalBgColor(DEFAULT_BG_COLOR)}
                      className="flex items-center gap-1 rounded-[4px] px-2 py-1 text-[10px] font-medium"
                      style={{ border: '1px solid var(--border-default)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}
                      title="Restaurar padrão"
                    >
                      <RotateCcw size={10} /> Padrão
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-6 gap-1.5">
                    {BACKGROUNDS.map((option) => (
                      <button key={option.value} type="button" onClick={() => setLocalBgColor(option.value)}
                        className="flex flex-col items-center gap-0.5 rounded-[4px] px-1 py-1.5"
                        style={{ border: `1px solid ${normalizeColorValue(localBgColor) === normalizeColorValue(option.value) ? '#3B82F6' : 'var(--border-default)'}`, background: normalizeColorValue(localBgColor) === normalizeColorValue(option.value) ? 'rgba(59,130,246,0.12)' : 'var(--bg-elevated)' }}
                      >
                        <span className="block h-4 w-full rounded-[3px] border" style={{ background: option.value, borderColor: 'rgba(0,0,0,0.12)' }} />
                        <span className="mt-0.5 block w-full truncate text-center text-[9px] font-medium leading-tight" style={{ color: 'var(--text-secondary)' }}>{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Divider */}
                <div style={{ height: 1, background: 'var(--border-default)' }} />

                {/* Cor da grade */}
                <div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--text-secondary)' }}>Cor da grade</div>
                      <div className="mt-0.5 text-[11px]" style={{ color: 'var(--text-secondary)' }}>Ajuste a visibilidade da malha.</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setLocalGridColor(DEFAULT_GRID_COLOR)}
                      className="flex items-center gap-1 rounded-[4px] px-2 py-1 text-[10px] font-medium"
                      style={{ border: '1px solid var(--border-default)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}
                      title="Restaurar padrão"
                    >
                      <RotateCcw size={10} /> Padrão
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-6 gap-1.5">
                    {GRID_COLORS.map((color) => (
                      <button key={color.value} type="button" onClick={() => setLocalGridColor(color.value)}
                        className="flex flex-col items-center gap-0.5 rounded-[4px] px-1 py-1.5"
                        style={{ border: `1px solid ${normalizeColorValue(localGridColor) === normalizeColorValue(color.value) ? '#3B82F6' : 'var(--border-default)'}`, background: normalizeColorValue(localGridColor) === normalizeColorValue(color.value) ? 'rgba(59,130,246,0.12)' : 'var(--bg-elevated)' }}
                      >
                        <span className="block h-4 w-full rounded-[3px] border" style={{ background: color.value, borderColor: 'rgba(15,23,42,0.12)' }} />
                        <span className="mt-0.5 block w-full truncate text-center text-[9px] font-medium leading-tight" style={{ color: 'var(--text-secondary)' }}>{color.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {activeSection === 'arrowColor' && (
              <section className="flex min-h-0 flex-col rounded-[4px] p-4" style={panelStyle}>
                <div className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--text-secondary)' }}>Cor das setas</div>
                <div className="mt-1 text-[11px]" style={{ color: 'var(--text-secondary)' }}>Define a leitura das conexões.</div>
                <div className="mt-3 grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))' }}>
                  {ARROW_COLORS.map((color) => (
                    <button key={color.value} type="button" onClick={() => setLocalArrowColor(color.value)}
                      className="flex flex-col items-center gap-1 rounded-[4px] px-2 py-3"
                      style={{ border: `1px solid ${normalizeColorValue(localArrowColor) === normalizeColorValue(color.value) ? '#3B82F6' : 'var(--border-default)'}`, background: normalizeColorValue(localArrowColor) === normalizeColorValue(color.value) ? 'rgba(59,130,246,0.12)' : 'var(--bg-elevated)' }}
                    >
                      <div className="flex h-8 w-full items-center justify-center"><MoveRight size={24} style={{ color: color.value }} /></div>
                      <span className="block w-full truncate text-center text-[10px] font-medium leading-tight" style={{ color: 'var(--text-secondary)' }}>{color.label}</span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {activeSection === 'layout' && !isFactory && (
              <section className="flex min-h-0 flex-col rounded-[4px] p-4" style={panelStyle}>
                <div className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--text-secondary)' }}>Layout dos cards</div>
                <div className="mt-1 text-[11px]" style={{ color: 'var(--text-secondary)' }}>Escolha a estrutura visual do card.</div>
                <div className="mt-3 grid gap-2" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                  {LAYOUTS.map((option) => (
                    <button key={option.id} type="button" onClick={() => setLocalLayout(option.id)}
                      className="rounded-[4px] p-2 text-left"
                      style={{ border: `1px solid ${localLayout === option.id ? '#3B82F6' : 'var(--border-default)'}`, background: localLayout === option.id ? 'rgba(59,130,246,0.12)' : 'var(--bg-elevated)' }}
                    >
                      <div className="h-14">{renderCard(option.id, palette)}</div>
                      <div className="mt-1.5 text-[11px] font-semibold">{option.label}</div>
                      <div className="mt-0.5 text-[10px] leading-3" style={{ color: 'var(--text-secondary)' }}>{option.description}</div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {activeSection === 'cardColor' && !isFactory && (
              <section className="flex min-h-0 flex-col rounded-[4px] p-4" style={panelStyle}>
                <div className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--text-secondary)' }}>Cor padrão dos cards</div>
                <div className="mt-1 flex items-center justify-between gap-3 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
                  <span>Paleta semântica usada no mapa.</span>
                  <span className="rounded-[4px] px-2 py-1 font-medium" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', color: 'var(--text-primary)' }}>{palette.label}</span>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {availableCardColors.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      title={option.label}
                      onClick={() => setLocalCardColor(option.value)}
                      className="rounded-[4px] p-2 text-left"
                      style={{
                        border: `1px solid ${localCardColor === option.value ? '#3B82F6' : 'var(--border-default)'}`,
                        background: localCardColor === option.value ? 'rgba(59,130,246,0.12)' : 'var(--bg-elevated)',
                      }}
                    >
                      <div className="relative h-[54px] overflow-hidden rounded-[3px]">
                        {renderCard(localLayout, option)}
                        {localCardColor === option.value && (
                          <span className="absolute right-1 top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#2563EB] text-white">
                            <Check size={9} strokeWidth={2.4} />
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-[10px] font-medium" style={{ color: 'var(--text-secondary)' }}>
                        {option.label}
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {activeSection === 'cardColor' && isFactory && (
              <section className="flex min-h-0 flex-col gap-5 rounded-[4px] p-4" style={panelStyle}>
                {/* Sketch / Blueprint toggle */}
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--text-secondary)' }}>Estilo dos ícones VSM</div>
                  <div className="mt-1 text-[11px]" style={{ color: 'var(--text-secondary)' }}>Define como os elementos do mapa são renderizados.</div>
                  <div className="mt-3 grid grid-cols-3 gap-3">
                    {[
                      { id: 'sketch' as const, label: '◻ Sketch', description: 'Ícones de linha clássicos' },
                      { id: 'blueprint' as const, label: '⬡ Blueprint', description: 'Planta CAD premium' },
                      { id: 'technical' as const, label: '▭ Técnico', description: 'Linhas retas, tinta preta' },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setLocalVsmStyle(opt.id)}
                        className="rounded-[4px] p-3 text-left"
                        style={{
                          border: `1px solid ${localVsmStyle === opt.id ? '#3B82F6' : 'var(--border-default)'}`,
                          background: localVsmStyle === opt.id ? 'rgba(59,130,246,0.12)' : 'var(--bg-elevated)',
                        }}
                      >
                        <div className="text-[12px] font-semibold">{opt.label}</div>
                        <div className="mt-0.5 text-[10px] leading-tight" style={{ color: 'var(--text-secondary)' }}>{opt.description}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ height: 1, background: 'var(--border-default)' }} />
                {/* VSM color palette */}
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.08em]" style={{ color: 'var(--text-secondary)' }}>Cor dos elementos VSM</div>
                  <div className="mt-1 text-[11px]" style={{ color: 'var(--text-secondary)' }}>Aplicada nos ícones nos modos Sketch e Blueprint.</div>
                  <div className="mt-3 grid grid-cols-5 gap-2">
                    {VSM_COLORS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        title={opt.label}
                        onClick={() => setLocalVsmCardColor(opt.value)}
                        className="flex flex-col items-center gap-1 rounded-[4px] px-1 py-2"
                        style={{
                          border: `1px solid ${localVsmCardColor === opt.value ? '#3B82F6' : 'var(--border-default)'}`,
                          background: localVsmCardColor === opt.value ? 'rgba(59,130,246,0.12)' : 'var(--bg-elevated)',
                        }}
                      >
                        <span className="block h-5 w-full rounded-[3px]" style={{ background: opt.value }} />
                        <span className="block w-full truncate text-center text-[9px] font-medium leading-tight" style={{ color: 'var(--text-secondary)' }}>{opt.label}</span>
                        {localVsmCardColor === opt.value && (
                          <Check size={8} strokeWidth={3} style={{ color: '#3b82f6' }} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </main>

          <aside className="hidden min-h-0 overflow-hidden border-l px-3 py-3 lg:block" style={{ borderColor: 'var(--border-default)', background: 'var(--bg-elevated)' }}>
            <div className="text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ color: 'var(--text-secondary)' }}>Preview</div>
            <div className="mt-3 overflow-hidden rounded-[4px]" style={panelStyle}>
              <div className="flex items-center justify-between px-3 py-2" style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border-default)' }}><div className="text-[11px] font-semibold">Workspace</div><div className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>{palette.label}</div></div>
              <div className="p-3">
                <div className="overflow-hidden rounded-[4px] border" style={{ borderColor: 'var(--border-default)' }}>
                  <div className="relative h-[132px]" style={canvasStyle}>
                    <div className="absolute left-3 top-[39px] h-[54px] w-[128px]">{renderCard(localLayout, palette)}</div>
                    <div className="absolute left-[145px] top-[65px] flex items-center gap-1"><div className="h-[2px] w-8 rounded-full" style={{ background: localArrowColor }} /><MoveRight size={12} style={{ color: localArrowColor }} /></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-[4px] p-4" style={panelStyle}>
              <div className="space-y-2 text-[11px]">
                <div className="flex items-start justify-between gap-3"><span style={{ color: 'var(--text-secondary)' }}>Fundo</span><span className="font-medium">{localBgColor}</span></div>
                <div className="flex items-start justify-between gap-3"><span style={{ color: 'var(--text-secondary)' }}>Grid</span><span className="font-medium">{localGridType}</span></div>
                {isFactory ? (
                  <>
                    <div className="flex items-start justify-between gap-3"><span style={{ color: 'var(--text-secondary)' }}>Estilo</span><span className="font-medium">{localVsmStyle}</span></div>
                    <div className="flex items-center justify-between gap-3"><span style={{ color: 'var(--text-secondary)' }}>Cor VSM</span><span className="h-4 w-12 rounded-[3px] border" style={{ background: localVsmCardColor, borderColor: 'rgba(0,0,0,0.15)' }} /></div>
                  </>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-3"><span style={{ color: 'var(--text-secondary)' }}>Layout</span><span className="font-medium">{localLayout}</span></div>
                    <div className="flex items-start justify-between gap-3"><span style={{ color: 'var(--text-secondary)' }}>Card</span><span className="font-medium">{palette.label}</span></div>
                  </>
                )}
              </div>
            </div>
          </aside>
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-2.5" style={{ background: 'var(--bg-elevated)', borderTop: '1px solid var(--border-default)' }}>
          <div className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>Ajustes aplicados diretamente ao canvas do mapa.</div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="rounded-[4px] px-4 py-2 text-xs font-medium" style={{ border: '1px solid var(--border-default)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}>Cancelar</button>
            <button type="button" onClick={handleSave} className="flex items-center gap-2 rounded-[4px] px-4 py-2 text-xs font-medium text-white" style={{ background: '#2563EB', border: '1px solid #1D4ED8' }}><Save size={14} />Aplicar configurações</button>
          </div>
        </div>
      </div>
    </div>
  );
};
